import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { NavMarker } from 'react-rhumb';
import { Flex } from 'foundations-components/components/Flex';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import useDataPrivacyConfig from '../hooks/use-data-privacy-config';
import Loading from '../components/Loading';
import RequestForm from '../components/RequestForm';
import { useTrackPageView } from '../hooks/use-tracking';
import { trackInteraction } from '../utils/usageTracker';
import LocaleFormattedMessage from '../components/locales/LocaleFormattedMessage';
import useLocaleContext from '../hooks/useLocaleContext';
import { ErrorMessage } from '../components/ErrorMessage';
import { getApolloErrorStatusCode } from '../utils/getApolloErrorStatusCode';
import { INTERACTIONS, TRACKING_SCREENS } from '../constants/TrackingConstants';
import { getRequestTypes, isSubmitButtonDisabled } from '../utils/form';
import useMediaQuery, { MEDIA_QUERY_BREAKPOINTS } from '../hooks/use-media-query';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function DataPrivacyRequestContainer() {
  var _dataPrivacyConfig$br, _dataPrivacyConfig$br2;
  const {
    slug
  } = useParams();
  const [requestType, setRequestType] = useState(undefined);
  const [country, setCountry] = useState('');
  const [email, setEmail] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const {
    locale
  } = useLocaleContext();
  const isMobile = useMediaQuery(MEDIA_QUERY_BREAKPOINTS.sm);
  useTrackPageView({
    screen: TRACKING_SCREENS.REQUEST_FORM,
    slug,
    locale
  });
  const {
    dataPrivacyConfig,
    errorFetchingConfig,
    loading,
    requestSubmitted,
    submitRequest
  } = useDataPrivacyConfig(slug);
  const onSubmit = useCallback(() => {
    if (!requestType) {
      return;
    }
    submitRequest(Object.assign({
      requestType,
      // TODO: Next PR, figure out if I can use the BE locales - or ask them if they can add FE locales to swagger
      locale: locale,
      email
    }, country && {
      country
    }, {
      // recaptchaToken is not actually optional. BE will 400 if it's not passed.
      recaptchaToken
    }));
    trackInteraction({
      interaction: INTERACTIONS.SUBMIT_REQUEST,
      slug,
      locale,
      country
    });
  }, [requestType, country, email, recaptchaToken, locale, slug, submitRequest]);
  const requestTypes = useMemo(() => getRequestTypes(dataPrivacyConfig), [dataPrivacyConfig]);
  const submitButtonDisabled = useMemo(() => isSubmitButtonDisabled(dataPrivacyConfig, requestType, country, email, recaptchaToken), [dataPrivacyConfig, requestType, country, email, recaptchaToken]);
  if (loading) {
    return /*#__PURE__*/_jsx(Loading, {});
  }
  if (errorFetchingConfig || !dataPrivacyConfig) {
    const statusCode = errorFetchingConfig && getApolloErrorStatusCode(errorFetchingConfig);
    if (statusCode === 429) {
      return /*#__PURE__*/_jsx(ErrorMessage, {
        title: /*#__PURE__*/_jsx(LocaleFormattedMessage, {
          message: "app.error.rateLimitUI.title"
        }),
        body: /*#__PURE__*/_jsx(LocaleFormattedMessage, {
          message: "app.error.rateLimitUI.body"
        })
      });
    } else {
      return /*#__PURE__*/_jsx(ErrorMessage, {
        title: /*#__PURE__*/_jsx(LocaleFormattedMessage, {
          message: "app.error.fetch.title"
        }),
        body: /*#__PURE__*/_jsx(LocaleFormattedMessage, {
          message: "app.error.fetch.body"
        })
      });
    }
  }
  if (requestSubmitted) {
    return /*#__PURE__*/_jsx(View, {
      "data-test-id": "request-submitted",
      blockSize: "100vh",
      children: /*#__PURE__*/_jsxs(Flex, {
        justifyContent: "center",
        flexDirection: "column",
        alignItems: "center",
        paddingInline: "200",
        marginBlockStart: "500",
        minBlockSize: "50vh",
        children: [/*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(LocaleFormattedMessage, {
            message: "app.submitted.title"
          })
        }), /*#__PURE__*/_jsx(Text, {
          as: "p",
          textAlign: "center",
          children: /*#__PURE__*/_jsx(LocaleFormattedMessage, {
            message: "app.submitted.body"
          })
        })]
      })
    });
  }
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "INDEX_LOAD",
    children: /*#__PURE__*/_jsx(View, {
      backgroundColor: isMobile ? 'fill-surface-default' : 'border-core-subtle',
      minBlockSize: "100vh",
      "data-test-id": "form-container",
      children: /*#__PURE__*/_jsx(RequestForm, {
        country: country,
        email: email,
        logoAltText: dataPrivacyConfig === null || dataPrivacyConfig === void 0 || (_dataPrivacyConfig$br = dataPrivacyConfig.branding) === null || _dataPrivacyConfig$br === void 0 ? void 0 : _dataPrivacyConfig$br.logoAltText,
        logoUrl: dataPrivacyConfig === null || dataPrivacyConfig === void 0 || (_dataPrivacyConfig$br2 = dataPrivacyConfig.branding) === null || _dataPrivacyConfig$br2 === void 0 ? void 0 : _dataPrivacyConfig$br2.logoUrl,
        onChangeCountry: setCountry,
        onChangeEmail: setEmail,
        onChangeRecaptchaToken: setRecaptchaToken,
        onChangeRequestType: setRequestType,
        onSubmit: onSubmit,
        requestType: requestType,
        requestTypes: requestTypes,
        submitButtonDisabled: submitButtonDisabled,
        businessEmail: dataPrivacyConfig === null || dataPrivacyConfig === void 0 ? void 0 : dataPrivacyConfig.email,
        isRecaptchaEnabled: !!(dataPrivacyConfig !== null && dataPrivacyConfig !== void 0 && dataPrivacyConfig.recaptchaEnabled),
        showCountrySetting: dataPrivacyConfig === null || dataPrivacyConfig === void 0 ? void 0 : dataPrivacyConfig.showCountrySetting
      })
    })
  });
}
export default DataPrivacyRequestContainer;